import { SelectionCountdownTimer } from '@/components/selection/selection-countdown-timer';
import { useSelectionState } from '@/hooks/use-selection-state';
import { useServerTimeNow } from '@/hooks/use-server-time-now';
import type { HackerTrackSelectionsShow, TrackWithTeams } from '@/types';
import { TracksOverview } from './components/TracksOverview';
import { TrackSelectionGrid } from './components/TrackSelectionGrid';

const POLL_URL = '/api/tracks/availability';

export default function Show({
  teamId,
  currentTrackId,
  trackSelectionStartTime,
  trackSelectionEndTime,
  serverNow,
  selectionClosed: initialSelectionClosed,
  tracksWithTeams,
}: HackerTrackSelectionsShow) {
  const serverTimeNow = useServerTimeNow(serverNow);

  const { startTime, selectionStarted, selectionClosed, withTeams } =
    useSelectionState<TrackWithTeams[]>({
      pollUrl: POLL_URL,
      initialStartTime: trackSelectionStartTime,
      initialEndTime: trackSelectionEndTime,
      initialWithTeams: initialSelectionClosed ? tracksWithTeams : null,
      serverTimeNow,
    });

  return (
    <div className="theme-ph26 selection-on-light min-h-screen bg-background font-mono text-foreground">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl space-y-8">
          <div className="text-center">
            <h1 className="inline-block font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
              Selección de{' '}
              <span className="bg-primary px-2 py-1 text-background">
                Tracks
              </span>
            </h1>
            <p className="mt-2 text-muted-foreground">
              Elige el track en el que competirá tu equipo
            </p>
          </div>

          {!startTime && (
            <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
              <div className="space-y-4">
                <div className="border-primary/20 border-b pb-4">
                  <h2 className="font-bold text-2xl text-primary sm:text-3xl md:text-4xl">
                    La selección de tracks no se ha activado aún
                  </h2>
                </div>
                <div className="border-primary border-l-4 pl-4">
                  <p className="text-primary/70 text-sm sm:text-base">
                    El administrador del evento aún no ha configurado el horario
                    de selección de tracks
                  </p>
                </div>
              </div>
            </div>
          )}

          {startTime && !selectionStarted && (
            <SelectionCountdownTimer
              targetTime={startTime}
              serverTimeNow={serverTimeNow}
              heading="La selección de tracks comienza pronto"
              subheading="Podrás elegir tu track cuando el contador llegue a cero"
            />
          )}

          {selectionStarted &&
            (selectionClosed ? (
              withTeams ? (
                <TracksOverview tracksWithTeams={withTeams} />
              ) : (
                <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
                  <div className="border-primary border-l-4 pl-4">
                    <p className="font-bold text-lg text-primary sm:text-xl">
                      La selección de tracks ha cerrado
                    </p>
                  </div>
                </div>
              )
            ) : teamId ? (
              <>
                <div className="border-2 border-primary bg-background/80 p-4 backdrop-blur-sm">
                  <div className="border-primary border-l-4 pl-4">
                    <p className="text-primary/70 text-sm sm:text-base">
                      Puedes cambiar el track de tu equipo libremente hasta que
                      cierre la selección.
                    </p>
                  </div>
                </div>
                <TrackSelectionGrid
                  teamId={teamId}
                  currentTrackId={currentTrackId}
                />
              </>
            ) : (
              <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
                <div className="border-primary border-l-4 pl-4">
                  <p className="font-bold text-lg text-primary sm:text-xl">
                    Aún no tienes equipo
                  </p>
                  <p className="mt-2 text-primary/70 text-sm sm:text-base">
                    Necesitas formar parte de un equipo para seleccionar un
                    track.
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
